import React, { createContext, useContext, useState } from 'react';
import { NumberArray } from '@wonderlandengine/api';
import { CloudData } from '../data/cloud-data.js';
import { Configuration, PaintTypes, BespokePaintColours } from '../data/configuration.js';
import { PaintConfigurator } from '../configurator/paint-configurator.js';
import { InteriorConfigurator } from '../configurator/interior-configurator.js';
import { ExteriorConfigurator } from '../configurator/exterior-configurator.js';
import { VehicleView } from './vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from './view-content.js';
import SelectedSectionItem from './ui/selected-section-item.tsx';
import Modal, { ModalContent } from './ui/modal.tsx'
import ViewContentRows from './ui/view-content-rows.tsx';
import BespokeColourPicker, { DEFAULT_BESPOKE_COLOUR, rgbToHex } from './ui/bespoke-colour-picker.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface PaintworkViewProps {
	paintConfigurator: PaintConfigurator;
	interiorConfigurator: InteriorConfigurator;
	exteriorConfigurator: ExteriorConfigurator;
	onBackButtonClicked: () => void;
	onNextButtonClicked: () => void;
}

interface PaintViewProps {
	subHeading: string;
	paintType: PaintTypes;
	itemId: string;
	itemName: string;
	sectionPrice: number;
	modalContent: ModalContent[];
	isClosed: boolean;
	bespokeColour: NumberArray | null;
	isBespokeSelected: boolean;
	onPanelOpenClose: (isOpen: boolean) => void;
	onItemClicked: (id: string) => void;
	onBespokeColourChanged: (rgba: NumberArray, paintType: PaintTypes) => void;
}

interface BespokePaintOptionProps {
	colour: NumberArray | null;
	isSelected: boolean;
	isPanelClosed: boolean;
	onColourChanged: (rgba: NumberArray) => void;
}

interface PaintItemsProps {
	paintType: PaintTypes;
}

interface PaintViewContextType {
	selectedItemId: string;
	handleItemClicked: (id: string) => void;
}

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

// the back-office option id a bespoke colour is recorded as, per paint type. a paint type absent from this map does not offer the
// bespoke 'select your own' option at all - satin and solitallic are fixed-colour only - so this one map decides both which panels
// carry the option and what selecting it records.
const BESPOKE_PAINT_OPTION_IDS: Partial<Record<PaintTypes, string>> = {
	[PaintTypes.Solid]: BespokePaintColours.Solid,
	[PaintTypes.Metallic]: BespokePaintColours.Metallic
};

// the icon the bespoke swatch shows until a colour has been chosen, so that the option reads as an invitation to choose one rather
// than as a colour already picked out for the customer. as with every configuration item image, the path is relative to the deploy
// root - 'static/' is dropped at runtime.
const SPECIAL_COLOUR_IMAGE_FILE_PATH = 'assets/images/configuration-items/paintwork/special-colour.webp';

// the information shown for a bespoke colour, both behind the panel's info icon while one is selected and on the customer's first
// selection of one. it replaces the back-office description of the bespoke option ids rather than adding to it.
const SPECIAL_PAINT_MODAL_CONTENT: ModalContent[] = [{
	title: 'SPECIAL PAINT INFORMATION',
	body: "All colours, textures and finishes represented in the Morgan Car Configurator will vary in appearance depending upon the device screen, or printer that is being used to view them. Therefore, exact finishes should be used for guidance only and checked with your Morgan Dealer. Morgan Motor Company are not able to replicate colours generated from the 'Special' colour functions on the Morgan Car Configurator. Suitable paint codes are required to produce special paint colours."
}];

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function PaintworkView(props: PaintworkViewProps) {

	// both models present the paint colours as an accordion of one panel per paint type, but the satin type is offered on the Supersport 400 only.
	const hasSatinPaintType = Configuration.isSupersport400Model();

	// initial values.
	const initialBodyColourItemId = Configuration.bodyPaintColour;
	const initialBodyColourItemName = () => getItemName(initialBodyColourItemId);
	const initialPaintType = CloudData.getPaintTypeForId(initialBodyColourItemId);

	// a bespoke colour outlives this view, which is unmounted on every change of step, so both halves of it are read back from the
	// configuration rather than restarting from the defaults: the paint type it was applied with is the one its option id belongs to.
	const isBespokeColourSelected = Configuration.isBespokePaintColour(initialBodyColourItemId);
	const initialBespokeColour = Configuration.bespokePaintColour;
	const initialBespokePaintType = isBespokeColourSelected ? initialPaintType : null;

	// states.
	const [selectedBodyColourItemId, setSelectedBodyColourItemId] = useState(initialBodyColourItemId);
	const [selectedBodyColourItemName, setSelectedBodyColourItemName] = useState(initialBodyColourItemName);

	// the one paint type panel currently open, or null when the customer has closed them all. holding the accordion as a single
	// value rather than as a flag per panel is what keeps opening and closing a panel to one line. the panel open on arrival is
	// the one holding the body's own colour, so that its current selection is in view.
	const [openPaintType, setOpenPaintType] = useState<PaintTypes | null>(initialPaintType);

	// the bespoke colour is shared by every panel, so that a colour chosen in one can then be seen in another type's finish -
	// which is the point of the option. it is null until the customer has chosen a colour, which is what has each swatch show the
	// 'special colour' icon rather than a colour it was never offered. the paint type it is currently applied with is held alongside
	// it, and is null whenever a standard colour is the selected one, so that only the panel matching the applied finish shows as selected.
	const [bespokeColour, setBespokeColour] = useState<NumberArray | null>(initialBespokeColour);
	const [bespokePaintType, setBespokePaintType] = useState<PaintTypes | null>(initialBespokePaintType);

	// handlers.

	const handleBodyColourItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedBodyColourItemId(id);
		setSelectedBodyColourItemName(getItemName(id));

		// a standard colour is now selected, so the bespoke colour is no longer the applied one.
		setBespokePaintType(null);

		// update the configuration.
		Configuration.bodyPaintColour = id;
		Configuration.updateCosts();

		// select the body paint colour on the car.
		props.paintConfigurator.selectBodyPaint(id);

		// select the dashboard option on the car.
		props.interiorConfigurator.selectDashboardOption(Configuration.dashboardOption);
	};

	const handlePanelOpenClose = (paintTypePanel: PaintTypes, isOpen: boolean) => {

		// only one panel is open at a time, so opening one closes the others, and closing one leaves none open.
		setOpenPaintType(isOpen ? paintTypePanel : null);
	};

	const handleBespokeColourChanged = (rgba: NumberArray, paintType: PaintTypes) => {

		// remember the colour and the paint type it was applied with, so that every panel shows the same colour and only that panel shows as selected.
		setBespokeColour(rgba);
		setBespokePaintType(paintType);

		// keep the colour in the configuration too, so that it survives this view being unmounted on a change of step.
		Configuration.bespokePaintColour = rgba;

		// record the selection as this paint type's bespoke option id, which is what gives the panel its description and price.
		// this handler fires on every pointer move while the picker is dragged, so the re-pricing is skipped once that id is already
		// the selected one - from then on only the colour changes, and the id, its description and its price do not.
		const bespokeOptionId = BESPOKE_PAINT_OPTION_IDS[paintType];
		if (bespokeOptionId !== undefined && Configuration.bodyPaintColour !== bespokeOptionId) {

			// update the selected item and item name.
			setSelectedBodyColourItemId(bespokeOptionId);
			setSelectedBodyColourItemName(getItemName(bespokeOptionId));

			// update the configuration.
			Configuration.bodyPaintColour = bespokeOptionId;
			Configuration.updateCosts();

			// rebuild the dashboard face, so that a body-matching dashboard picks up this paint type's finish. this is the expensive
			// call, so it belongs here rather than below - the colour it starts from is corrected on the very next line anyway.
			props.interiorConfigurator.selectDashboardOption(Configuration.dashboardOption);
		}

		// apply the bespoke colour to the car in real time, over the base material of that paint type.
		props.paintConfigurator.selectBespokeBodyPaint(rgba, paintType);

		// and keep a body-matching dashboard face with it. this is only an albedo write, so it is cheap enough to run on every
		// movement of the picker, and does nothing at all when the selected dashboard option does not follow the body colour.
		props.interiorConfigurator.updateDashboardBodyColour(rgba);
	};

	// return the HTML.
	return (
		<VehicleView
			onBackButtonClicked={props.onBackButtonClicked}
			onNextButtonClicked={props.onNextButtonClicked}>
			<div id="view-content">
				<div id="view-content-header">
					<h2 id="ga4-paintwork">
						PAINTWORK
					</h2>
				</div>

				<PaintView
					subHeading={'SOLID'}
					paintType={PaintTypes.Solid}
					itemId={selectedBodyColourItemId}
					itemName={selectedBodyColourItemName}
					onItemClicked={handleBodyColourItemClicked}
					sectionPrice={CloudData.getPrice(selectedBodyColourItemId, CloudData.paintworkColourOptions, { ref: false })}
					modalContent={getModalContent(selectedBodyColourItemId)}
					isClosed={openPaintType !== PaintTypes.Solid}
					onPanelOpenClose={(isOpen) => handlePanelOpenClose(PaintTypes.Solid, isOpen)}
					bespokeColour={bespokeColour}
					isBespokeSelected={bespokePaintType === PaintTypes.Solid}
					onBespokeColourChanged={handleBespokeColourChanged}
				/>
				<PaintView
					subHeading={'METALLIC'}
					paintType={PaintTypes.Metallic}
					itemId={selectedBodyColourItemId}
					itemName={selectedBodyColourItemName}
					onItemClicked={handleBodyColourItemClicked}
					sectionPrice={CloudData.getPrice(selectedBodyColourItemId, CloudData.paintworkColourOptions, { ref: false })}
					modalContent={getModalContent(selectedBodyColourItemId)}
					isClosed={openPaintType !== PaintTypes.Metallic}
					onPanelOpenClose={(isOpen) => handlePanelOpenClose(PaintTypes.Metallic, isOpen)}
					bespokeColour={bespokeColour}
					isBespokeSelected={bespokePaintType === PaintTypes.Metallic}
					onBespokeColourChanged={handleBespokeColourChanged}
				/>
				{hasSatinPaintType &&
					<PaintView
						subHeading={'SATIN'}
						paintType={PaintTypes.Satin}
						itemId={selectedBodyColourItemId}
						itemName={selectedBodyColourItemName}
						onItemClicked={handleBodyColourItemClicked}
						sectionPrice={CloudData.getPrice(selectedBodyColourItemId, CloudData.paintworkColourOptions, { ref: false })}
						modalContent={getModalContent(selectedBodyColourItemId)}
						isClosed={openPaintType !== PaintTypes.Satin}
						onPanelOpenClose={(isOpen) => handlePanelOpenClose(PaintTypes.Satin, isOpen)}
						bespokeColour={bespokeColour}
						isBespokeSelected={bespokePaintType === PaintTypes.Satin}
						onBespokeColourChanged={handleBespokeColourChanged}
					/>
				}
				<PaintView
					subHeading={'SOLITALLIC'}
					paintType={PaintTypes.Solitallic}
					itemId={selectedBodyColourItemId}
					itemName={selectedBodyColourItemName}
					onItemClicked={handleBodyColourItemClicked}
					sectionPrice={CloudData.getPrice(selectedBodyColourItemId, CloudData.paintworkColourOptions, { ref: false })}
					modalContent={getModalContent(selectedBodyColourItemId)}
					isClosed={openPaintType !== PaintTypes.Solitallic}
					onPanelOpenClose={(isOpen) => handlePanelOpenClose(PaintTypes.Solitallic, isOpen)}
					bespokeColour={bespokeColour}
					isBespokeSelected={bespokePaintType === PaintTypes.Solitallic}
					onBespokeColourChanged={handleBespokeColourChanged}
				/>

			</div>
		</VehicleView>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow the paint item fn to access the selected item id from the paint view and call its item click handler.
const PaintViewContext = createContext<PaintViewContextType | undefined>(undefined);

function PaintView(props: PaintViewProps) {

	// set the selected item ID from the given prop.
	const selectedItemId = props.itemId;

	// if an item is clicked, call the given handler.
	const handleItemClicked = (id: string) => {
		props.onItemClicked(id);
	};

	// the bespoke option is only shown on the paint types that offer it.
	const hasBespokeColourOption = BESPOKE_PAINT_OPTION_IDS[props.paintType] !== undefined;

	// return the HTML.
	return (
		<PaintViewContext.Provider value={{ selectedItemId, handleItemClicked }}>
			<SectionPanel
				subHeading={props.subHeading}
				modalContent={props.modalContent}
				isClosed={props.isClosed}
				onPanelOpenClose={props.onPanelOpenClose}
			>
				<SelectedSectionItem
					itemName={props.itemName}
					sectionPrice={props.sectionPrice}
				/>
				{hasBespokeColourOption &&
					<BespokePaintOption
						colour={props.bespokeColour}
						isSelected={props.isBespokeSelected}
						isPanelClosed={props.isClosed}
						onColourChanged={(rgba) => props.onBespokeColourChanged(rgba, props.paintType)}
					/>
				}
				<PaintItems paintType={props.paintType} />
			</SectionPanel>
		</PaintViewContext.Provider>
	);
}

// the 'select your own' option shown above the fixed colours in a paint panel: a swatch of the bespoke colour that expands into the colour picker.
// n.b. this is the natural home for bespoke-specific functionality, such as the special paint information shown on the first selection.
function BespokePaintOption(props: BespokePaintOptionProps) {

	// the colour has not been chosen until the customer has been in the picker, so until then the swatch shows the 'special colour'
	// icon instead of a fill, and the picker - and the colour applied when it is opened - falls back to its own default. this is the
	// one place that fallback is made, so the default itself stays owned by the portable picker.
	const hasChosenColour = props.colour !== null;
	const colour = props.colour ?? DEFAULT_BESPOKE_COLOUR;

	// states.
	const [isPickerOpen, setIsPickerOpen] = useState(false);

	// the picker seeds itself from 'initialColour' when it mounts and not afterwards, so it is re-mounted on each opening - keyed on
	// the number of times it has been opened - to start it on the shared colour as it stands now rather than the one it first saw.
	const [openCount, setOpenCount] = useState(0);

	// whether this option is showing the special paint information, which happens once only, on the customer's first selection of a
	// bespoke colour in any panel. once shown the modal is left mounted, as closing it only hides it.
	const [isShowingSpecialPaintInformation, setIsShowingSpecialPaintInformation] = useState(false);

	// close the picker whenever the panel it sits in closes, so that it is not still open when that panel is next opened.
	// this adjusts state during rendering rather than in an effect, which is react's pattern for resetting state from a prop change.
	const [wasPanelClosed, setWasPanelClosed] = useState(props.isPanelClosed);
	if (props.isPanelClosed !== wasPanelClosed) {
		setWasPanelClosed(props.isPanelClosed);
		if (props.isPanelClosed) {
			setIsPickerOpen(false);
		}
	}

	// handlers.

	const handleSwatchClicked = () => {

		// opening the picker re-applies the shared colour with this panel's paint type, which is how the same colour is compared
		// across finishes. closing it is left neutral, as a click to collapse the picker and go back to the fixed colours should
		// not re-apply anything.
		if (!isPickerOpen) {
			setOpenCount(openCount + 1);
			props.onColourChanged(colour);

			// the first selection of a bespoke colour also shows the special paint information. the flag lives in the configuration,
			// as this view is unmounted on every change of step, and is shared by every panel's option, so it is shown once in all.
			if (!Configuration.hasShownSpecialPaintInformation) {
				Configuration.hasShownSpecialPaintInformation = true;
				setIsShowingSpecialPaintInformation(true);
			}
		}

		// toggle the picker.
		setIsPickerOpen(!isPickerOpen);
	};

	// return the HTML.
	return (
		<div className="bespoke-paint-option">

			<div className="bespoke-paint-option-header">
				<a
					role="button"
					className={`bespoke-paint-option-swatch${hasChosenColour ? '' : ' default'}${props.isSelected ? ' selected' : ''}${isPickerOpen ? ' open' : ''}`}
					style={hasChosenColour ? { backgroundColor: rgbToHex(colour) } : undefined}
					title="Choose your own colour"
					onClick={handleSwatchClicked}
				>
					{/* before a colour is chosen the swatch is the icon rather than a fill - an image, as the fixed colours are, so it sizes with them. */}
					{!hasChosenColour &&
						<img draggable={false} className="bespoke-paint-option-swatch-icon" src={SPECIAL_COLOUR_IMAGE_FILE_PATH} alt="Special colour" />
					}
				</a>
				{/* the label doubles as the selected state, reading 'SELECTED' once this panel's bespoke colour is the one on the car. */}
				<p className="bespoke-paint-option-label">{props.isSelected ? 'SELECTED' : 'SELECT YOUR OWN'}</p>
			</div>

			<div className={`bespoke-paint-option-picker${isPickerOpen ? ' open' : ''}`}>
				<BespokeColourPicker key={openCount} initialColour={colour} onColourChanged={props.onColourChanged} />
			</div>

			{/* a modal given no trigger opens as it mounts. it sits outside the picker, whose scale transform would otherwise become */}
			{/* the containing block of its fixed position and shrink it into the picker's box. */}
			{isShowingSpecialPaintInformation &&
				<Modal content={SPECIAL_PAINT_MODAL_CONTENT} />
			}

		</div>
	);
}

function PaintItems(props: PaintItemsProps) {
	switch (props.paintType) {
		case PaintTypes.Metallic:
			return <PaintItemsMetallic />;
		case PaintTypes.Solitallic:
			return <PaintItemsSolitallic />;
		case PaintTypes.Satin:
			return <PaintItemsSatin />;
		default:
			return <PaintItemsSolid />;
	}
};

function PaintItemsSolid() {
	return (
		<ViewContentRows maxRowLength={8}>
			<PaintItem id={'PS006'} imageFilePath={'solid/gloss-solid-sport-black'} />
			<PaintItem id={'PS004'} imageFilePath={'solid/gloss-solid-sport-ivory'} />
			<PaintItem id={'PS001'} imageFilePath={'solid/gloss-solid-sport-green'} />
			<PaintItem id={'PS002'} imageFilePath={'solid/gloss-solid-sport-blue'} />
			<PaintItem id={'PS003'} imageFilePath={'solid/gloss-solid-sport-red'} />
			<PaintItem id={'PS005'} imageFilePath={'solid/gloss-solid-sport-dove-grey'} />
			<PaintItem id={'PS014'} imageFilePath={'solid/gloss-solid-classic-white'} />
			<PaintItem id={'PS015'} imageFilePath={'solid/gloss-solid-classic-dark-grey'} />
			<PaintItem id={'PS013'} imageFilePath={'solid/gloss-solid-classic-blue'} />
			<PaintItem id={'PS012'} imageFilePath={'solid/gloss-solid-classic-almond-green'} />
			<PaintItem id={'PS017'} imageFilePath={'solid/gloss-safari-yellow'} />
			<PaintItem id={'PS018'} imageFilePath={'solid/gloss-lagoon-blue'} />
			<PaintItem id={'PS022'} imageFilePath={'solid/gloss-belfast-blue'} />
			<PaintItem id={'PS023'} imageFilePath={'solid/gloss-vermiglia-red'} />
		</ViewContentRows>
	);
}

function PaintItemsMetallic() {
	return (
		<ViewContentRows maxRowLength={8}>
			<PaintItem id={'PM001'} imageFilePath={'metallic/metallic-black'} />
			<PaintItem id={'PM003'} imageFilePath={'metallic/dark-silver'} />
			<PaintItem id={'PM008'} imageFilePath={'metallic/metallic-graphite'} />
			<PaintItem id={'PM005'} imageFilePath={'metallic/ice-blue'} />
			<PaintItem id={'PM002'} imageFilePath={'metallic/metallic-silver'} />
			<PaintItem id={'PM007'} imageFilePath={'metallic/metallic-red'} />
			<PaintItem id={'PM006'} imageFilePath={'metallic/metallic-green'} />
			<PaintItem id={'PM013'} imageFilePath={'metallic/white-gold'} />
			<PaintItem id={'PM014'} imageFilePath={'metallic/alligator-green'} />
			<PaintItem id={'PM015'} imageFilePath={'metallic/regal-grey'} />
			<PaintItem id={'PM019'} imageFilePath={'metallic/edelweiss-green'} />
			<PaintItem id={'PM020'} imageFilePath={'metallic/furka-rouge'} />
			<PaintItem id={'PM024'} imageFilePath={'metallic/bahama-blue'} />
			<PaintItem id={'PM025'} imageFilePath={'metallic/armeria-lilac'} />
			<PaintItem id={'PM023'} imageFilePath={'metallic/sea-teal'} />
			<PaintItem id={'PM027'} imageFilePath={'metallic/metallic-hill-green'} />
			<PaintItem id={'PM028'} imageFilePath={'metallic/metallic-acer-red'} />
			<PaintItem id={'PM029'} imageFilePath={'metallic/metallic-riviera-blue'} />
		</ViewContentRows>
	);
}

function PaintItemsSolitallic() {
	return (
		<ViewContentRows>
			<PaintItem id={'PSM001'} imageFilePath={'solitallic/solitallic-sport-green-gloss'} />
			<PaintItem id={'PSM002'} imageFilePath={'solitallic/solitallic-sport-blue-gloss'} />
			<PaintItem id={'PSM003'} imageFilePath={'solitallic/solitallic-classic-white-gloss'} />
			<PaintItem id={'PM026'} imageFilePath={'solitallic/solitallic-harbour-grey'} />
		</ViewContentRows>
	);
}

function PaintItemsSatin() {
	return (
		<ViewContentRows>
			<PaintItem id={'ST01'} imageFilePath={'satin/satin-forged-grey'} />
			<PaintItem id={'ST04'} imageFilePath={'satin/satin-silk-silver'} />
			<PaintItem id={'ST03'} imageFilePath={'satin/satin-sail-silver'} />
			<PaintItem id={'ST02'} imageFilePath={'satin/satin-mineral-blue'} />
		</ViewContentRows>
	);
}

function PaintItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(PaintViewContext);
	if (!context) {
		throw new Error('PaintViewContext must be used within a PaintView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/paintwork/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedItemId}
			onItemClicked={context.handleItemClicked}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Public Functions:
//-----------------------------------------------------------------------------------------

// used by views that have a 'matching body colour' option in order to display a swatch showing the currently selected body colour.
export function getFilePathForGlossPaintColourId(id: string): string {

	// map the paint style IDs to the file paths for the relevant swatches.
	const filePaths: Record<string, string> = {

		// solid.
		PS006: 'solid/gloss-solid-sport-black',
		PS004: 'solid/gloss-solid-sport-ivory',
		PS001: 'solid/gloss-solid-sport-green',
		PS002: 'solid/gloss-solid-sport-blue',
		PS003: 'solid/gloss-solid-sport-red',
		PS005: 'solid/gloss-solid-sport-dove-grey',
		PS014: 'solid/gloss-solid-classic-white',
		PS015: 'solid/gloss-solid-classic-dark-grey',
		PS013: 'solid/gloss-solid-classic-blue',
		PS012: 'solid/gloss-solid-classic-almond-green',
		PS017: 'solid/gloss-safari-yellow',
		PS018: 'solid/gloss-lagoon-blue',
		PS022: 'solid/gloss-belfast-blue',
		PS023: 'solid/gloss-vermiglia-red',

		// metallic.
		PM001: 'metallic/metallic-black',
		PM003: 'metallic/dark-silver',
		PM008: 'metallic/metallic-graphite',
		PM005: 'metallic/ice-blue',
		PM002: 'metallic/metallic-silver',
		PM007: 'metallic/metallic-red',
		PM006: 'metallic/metallic-green',
		PM013: 'metallic/white-gold',
		PM014: 'metallic/alligator-green',
		PM015: 'metallic/regal-grey',
		PM019: 'metallic/edelweiss-green',
		PM020: 'metallic/furka-rouge',
		PM024: 'metallic/bahama-blue',
		PM025: 'metallic/armeria-lilac',
		PM023: 'metallic/sea-teal',
		PM027: 'metallic/metallic-hill-green',
		PM028: 'metallic/metallic-acer-red',
		PM029: 'metallic/metallic-riviera-blue',

		// solitallic.
		PSM001: 'solitallic/solitallic-sport-green-gloss',
		PSM002: 'solitallic/solitallic-sport-blue-gloss',
		PSM003: 'solitallic/solitallic-classic-white-gloss',
		PM026: 'solitallic/solitallic-harbour-grey',

		// satin.
		ST01: 'satin/satin-forged-grey',
		ST04: 'satin/satin-silk-silver',
		ST03: 'satin/satin-sail-silver',
		ST02: 'satin/satin-mineral-blue'
	};

	// return the corresponding path.
	const filePath = filePaths[id];
	if (filePath) {
		return filePath;
	}

	// or return a fail-save default if not found.
	return 'solid/gloss-solid-sport-black';
}

export function getFilePathForMattePaintColourId(id: string): string {

	// map the paint style IDs to the file paths for the relevant swatches.
	const filePaths: Record<string, string> = {

		// solid.
		PS006: 'solid/matte/matte-solid-sport-black',
		PS004: 'solid/matte/matte-solid-sport-ivory',
		PS001: 'solid/matte/matte-solid-sport-green',
		PS002: 'solid/matte/matte-solid-sport-blue',
		PS003: 'solid/matte/matte-solid-sport-red',
		PS005: 'solid/matte/matte-solid-sport-dove-grey',
		PS014: 'solid/matte/matte-solid-classic-white',
		PS015: 'solid/matte/matte-solid-classic-dark-grey',
		PS013: 'solid/matte/matte-solid-classic-blue',
		PS012: 'solid/matte/matte-solid-classic-almond-green',
		PS017: 'solid/matte/matte-safari-yellow',
		PS018: 'solid/matte/matte-lagoon-blue',
		PS022: 'solid/matte/matte-belfast-blue',
		PS023: 'solid/matte/matte-vermiglia-red',

		// metallic.
		PM001: 'metallic/matte/matte-metallic-black',
		PM003: 'metallic/matte/matte-metallic-dark-silver',
		PM008: 'metallic/matte/matte-metallic-graphite',
		PM005: 'metallic/matte/matte-metallic-ice-blue',
		PM002: 'metallic/matte/matte-metallic-silver',
		PM007: 'metallic/matte/matte-metallic-red',
		PM006: 'metallic/matte/matte-metallic-green',
		PM013: 'metallic/matte/matte-white-gold',
		PM014: 'metallic/matte/matte-alligator-green',
		PM015: 'metallic/matte/matte-regal-grey',
		PM019: 'metallic/matte/matte-edelweiss-green',
		PM020: 'metallic/matte/matte-furka-rouge',
		PM024: 'metallic/matte/matte-bahama-blue',
		PM025: 'metallic/matte/matte-armeria-lilac',
		PM023: 'metallic/matte/matte-sea-teal',
		PM027: 'metallic/matte/matte-metallic-hill-green',
		PM028: 'metallic/matte/matte-metallic-acer-red',
		PM029: 'metallic/matte/matte-metallic-riviera-blue',

		// solitalic.
		PSM001: 'solitallic/matte/solitallic-sport-green-matte',
		PSM002: 'solitallic/matte/solitallic-sport-blue-matte',
		PSM003: 'solitallic/matte/solitallic-classic-white-matte',
		PM026: 'solitallic/matte/solitallic-harbour-grey',

		// satin.
		ST01: 'satin/matte/satin-forged-grey-matte',
		ST04: 'satin/matte/satin-silk-silver-matte',
		ST03: 'satin/matte/satin-sail-silver-matte',
		ST02: 'satin/matte/satin-mineral-blue-matte'
	};

	// return the corresponding path.
	const filePath = filePaths[id];
	if (filePath) {
		return filePath;
	}

	// or return a fail-save default if not found.
	return 'solid/matte/matte-solid-sport-black';
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.paintworkColourOptions);
}

// returns the content for the info modal of the given paint id - the special paint information for a bespoke colour, whose option
// ids have no back-office description to show, or the option's own description for every standard colour.
function getModalContent(id: string): ModalContent[] {
	if (Configuration.isBespokePaintColour(id)) {
		return SPECIAL_PAINT_MODAL_CONTENT;
	}
	return CloudData.getModalContent(id, CloudData.paintworkColourOptions);
}
